import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, Download, Loader2, Users, TrendingUp } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, PieChart, Pie, Cell } from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['hsl(160, 60%, 35%)', 'hsl(25, 90%, 55%)', 'hsl(30, 30%, 60%)', 'hsl(220, 60%, 50%)', 'hsl(280, 60%, 50%)'];

const AnalysisPage = () => {
  const { policyId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    loadAnalysis();
  }, [policyId]);

  const loadAnalysis = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/analysis/${policyId}`);
      if (response.ok) {
        const analysisData = await response.json();
        setData(analysisData);
      } else {
        toast.error("Failed to load analysis");
        navigate('/admin');
      }
    } catch (error) {
      console.error(error);
      toast.error("An error occurred");
      navigate('/admin');
    }
    setLoading(false);
  };

  const handleExport = async () => {
    try {
      const response = await fetch(`${API}/export/${policyId}`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `policy_${policyId}_data.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success("Data exported successfully!");
      } else {
        toast.error("Failed to export data");
      }
    } catch (error) {
      console.error(error);
      toast.error("An error occurred");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-transparent to-accent/5 flex items-center justify-center">
        <div className="text-center" data-testid="loading-spinner">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-lg text-muted-foreground">Loading data...</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const ageGroupData = Object.entries(data.statistics.age_group_distribution).map(([name, value]) => ({
    name,
    responses: value
  }));

  const emotionalData = Object.entries(data.statistics.emotional_averages).map(([name, value]) => ({
    emotion: name.charAt(0).toUpperCase() + name.slice(1),
    score: value,
    fullMark: 10
  }));

  const riskAppetiteData = Object.entries(data.statistics.risk_appetite_distribution).map(([name, value]) => ({
    name: name.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
    value
  }));

  const investmentChangeData = Object.entries(data.statistics.investment_change_distribution).map(([name, value]) => ({
    name: name.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
    value
  }));

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-transparent to-accent/5 py-12">
      <div className="container mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between mb-8">
          <Button 
            data-testid="back-to-dashboard-button"
            variant="ghost" 
            onClick={() => navigate('/admin')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>

          <Button 
            data-testid="export-data-button"
            onClick={handleExport}
            className="bg-primary hover:bg-primary/90"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>

        {/* Policy Header */}
        <Card className="border border-border/50 shadow-lg rounded-xl mb-8">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="text-4xl mb-3">{data.policy.title}</CardTitle>
                <CardDescription className="text-base">{data.policy.description}</CardDescription>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Total Responses</p>
                <p className="text-4xl font-bold font-mono text-primary" data-testid="total-responses">{data.total_responses}</p>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Age Group Distribution */}
          <Card data-testid="age-distribution-chart" className="border border-border/50 shadow-sm rounded-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                Age Group Distribution
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={ageGroupData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="name" stroke="hsl(var(--foreground))" />
                  <YAxis stroke="hsl(var(--foreground))" />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '0.5rem'
                    }}
                  />
                  <Bar dataKey="responses" fill="hsl(160, 60%, 35%)" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Emotional Responses Radar */}
          <Card data-testid="emotional-radar-chart" className="border border-border/50 shadow-sm rounded-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                Emotional Response Profile
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <RadarChart data={emotionalData}>
                  <PolarGrid stroke="hsl(var(--border))" />
                  <PolarAngleAxis dataKey="emotion" stroke="hsl(var(--foreground))" />
                  <PolarRadiusAxis angle={90} domain={[0, 10]} stroke="hsl(var(--muted-foreground))" />
                  <Radar name="Average Score" dataKey="score" stroke="hsl(25, 90%, 55%)" fill="hsl(25, 90%, 55%)" fillOpacity={0.6} />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '0.5rem'
                    }}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Risk Appetite */}
          <Card data-testid="risk-appetite-chart" className="border border-border/50 shadow-sm rounded-xl">
            <CardHeader>
              <CardTitle>Risk Appetite Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={riskAppetiteData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry) => `${entry.name}: ${entry.value}`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {riskAppetiteData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '0.5rem'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Investment Change */}
          <Card data-testid="investment-change-chart" className="border border-border/50 shadow-sm rounded-xl">
            <CardHeader>
              <CardTitle>Investment Change Intent</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={investmentChangeData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis type="number" stroke="hsl(var(--foreground))" />
                  <YAxis dataKey="name" type="category" width={150} stroke="hsl(var(--foreground))" />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '0.5rem'
                    }}
                  />
                  <Bar dataKey="value" fill="hsl(30, 30%, 60%)" radius={[0, 8, 8, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AnalysisPage;
